import {
  GridColumnMenuProps,
  GridColumnMenuItemProps,
  useGridApiContext,
  GridPinnedColumnPosition,
} from '@mui/x-data-grid-premium'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { Box } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import FunctionsIcon from '@mui/icons-material/Functions'
import { useState } from 'react'

import { CustomColumnGridConfig } from './types'
import { useAppTheme } from '@/theme/ThemeContext'
import CustomDraggableManageColumnsMenu from './CustomManageColumnsMenu'

function CustomSortMenueItem(props: GridColumnMenuItemProps) {
  const apiRef = useGridApiContext()
  const { colDef } = props
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const colors = {
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    hoverBg: isDarkMode ? '#1D325A' : '#EFF2F7',
  }

  const handleSortUp = (event: React.MouseEvent) => {
    apiRef.current.sortColumn(colDef.field, 'asc')
    props.onClick(event)
  }
  const handleSortDown = (event: React.MouseEvent) => {
    apiRef.current.sortColumn(colDef.field, 'desc')
    props.onClick(event)
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '10px',
        gap: '10px',
      }}
    >
      <Box
        sx={{
          color: colors.text,
          cursor: 'pointer',
          justifyContent: 'flex-end',
          fontWeight: 300,
          fontStyle: 'light',
          fontSize: '14px',
          lineHeight: '100%',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: colors.hoverBg,
          },
        }}
        display="flex"
        alignItems="center"
        gap={1}
        onClick={handleSortUp}
      >
        מיון בסדר עולה
        <ArrowUpwardIcon fontSize="small" />
      </Box>
      <Box
        sx={{
          color: colors.text,
          cursor: 'pointer',
          fontWeight: 300,
          fontStyle: 'light',
          fontSize: '14px',
          lineHeight: '100%',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: colors.hoverBg,
          },
        }}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap={1}
        onClick={handleSortDown}
      >
        מיון בסדר יורד
        <ArrowDownwardIcon fontSize="small" />
      </Box>
    </Box>
  )
}

function CustomPinningMenu(props: GridColumnMenuItemProps) {
  const apiRef = useGridApiContext()
  const { colDef } = props
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const colors = {
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    hoverBg: isDarkMode ? '#1D325A' : '#EFF2F7',
  }

  const handlePinRight = () => {
    apiRef.current.pinColumn(colDef.field, GridPinnedColumnPosition.RIGHT)
  }
  const handleUnpin = (isPinnedRight: boolean, isPinnedLeft: boolean) => {
    if (isPinnedRight) {
      apiRef.current.unpinColumn(colDef.field)
    } else if (isPinnedLeft) {
      apiRef.current.unpinColumn(colDef.field)
    }
  }
  const pinnedColumns = apiRef.current.getPinnedColumns()
  const isPinnedRight = pinnedColumns?.right?.includes(colDef.field)
  const isPinnedLeft = pinnedColumns?.left?.includes(colDef.field)

  return (
    <Box sx={{ margin: '10px' }}>
      <Box
        onClick={() => {
          isPinnedRight || isPinnedLeft
            ? handleUnpin(isPinnedRight || false, isPinnedLeft || false)
            : handlePinRight()
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          color: colors.text,
          cursor: 'pointer',
          fontWeight: 300,
          fontStyle: 'light',
          fontSize: '14px',
          lineHeight: '100%',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: colors.hoverBg,
          },
        }}
      >
        {isPinnedRight || isPinnedLeft ? (
          <Box> ביטול הצמדת עמודה</Box>
        ) : (
          <Box>הצמד עמודה</Box>
        )}
        {isPinnedRight || isPinnedLeft ? (
          <PushPinOutlinedIcon />
        ) : (
          <PushPinOutlinedIcon />
        )}
      </Box>
    </Box>
  )
}

function CustomAggregationMenu(props: GridColumnMenuItemProps) {
  const [selectedValue, setSelectedValue] = useState<string>('')

  const options = [
    { label: 'SUM', value: 'sum' },
    { label: 'AVG', value: 'avg' },
    { label: 'Min', value: 'min' },
    { label: 'Max', value: 'max' },
  ]
  const apiRef = useGridApiContext()
  const { colDef } = props
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const colors = {
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    bg: isDarkMode ? '#0D192F' : '#F8FAFD',
    inputLabel: isDarkMode ? '#9CA3AF' : '#003F61',
    border: isDarkMode ? '#445876' : 'rgba(0,0,0,0.23)',
    menuBg: isDarkMode ? '#162645' : '#fff',
    menuItemHover: isDarkMode ? '#1D325A' : '#E3F5FF',
  }

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value
    setSelectedValue(newValue)
    apiRef.current.updateColumns([
      {
        ...colDef,
        cellClassName: (_params: any) => 'aggregated-cell',
      },
    ])
    apiRef.current.setAggregationModel({
      [colDef.field]: event.target.value,
      ['function']: event.target.value,
      ['name']: colDef.field,
    })
  }
  return (
    <Box sx={{ margin: '10px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          color: colors.text,
          cursor: 'pointer',
          fontWeight: 300,
          fontStyle: 'light',
          fontSize: '14px',
          lineHeight: '100%',
          gap: '2px',
        }}
      >
        <FormControl
          sx={{ minWidth: 120, backgroundColor: colors.bg }}
          size="small"
        >
          <InputLabel
            id="aggregation-select-label"
            sx={{
              fontSize: '12px',
              color: colors.inputLabel,
              fontWeight: 300,
              fontStyle: 'light',
              marginTop: '-2px',
              '&.Mui-focused': { color: colors.text },
            }}
          >
            צבירה
          </InputLabel>
          <Select
            labelId="aggregation-select-label"
            id="aggregation-select"
            label="צבירה"
            sx={{
              backgroundColor: colors.bg,
              color: colors.text,
              fontSize: '14px',
              height: '30px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode ? '#81B5FF' : 'rgba(0,0,0,0.87)',
              },
              '& .MuiSvgIcon-root': {
                color: colors.text,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: colors.menuBg,
                  color: colors.text,
                  '& .MuiMenuItem-root': {
                    '&:hover': {
                      bgcolor: colors.menuItemHover,
                    },
                    '&.Mui-selected': {
                      bgcolor: colors.menuItemHover,
                      '&:hover': { bgcolor: colors.menuItemHover },
                    },
                  },
                },
              },
            }}
            value={selectedValue}
            onChange={handleChange}
          >
            {options.map(option => (
              <MenuItem
                sx={{
                  fontSize: '14px',
                }}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FunctionsIcon fontSize="small" />
      </Box>
    </Box>
  )
}

const Divider = () => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  return (
    <Box
      sx={{ 
        backgroundColor: isDarkMode ? '#445876' : 'rgba(50, 59, 119, 0.10)', 
        height: '1px', 
        width: '100%' 
      }}
    ></Box>
  )
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  const { colDef, hideMenu } = props
  const x = colDef as CustomColumnGridConfig
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    bg: isDarkMode ? '#162645' : '#F8FAFD',
    text: isDarkMode ? '#FFFFFF' : '#003F61',
  }

  return (
    <Box
      onWheel={e => {
        e.stopPropagation()
      }}
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        paddingTop: '15PX',
        paddingRight: '15px',
        paddingLeft: '15px',
        paddingBottom: '10px',
        borderRadius: '4px',
      }}
    >
      {colDef.sortable !== false && (
        <>
          <CustomSortMenueItem colDef={colDef} onClick={hideMenu} />
          <Divider />
        </>
      )}
      <CustomPinningMenu colDef={colDef} onClick={hideMenu} />
      <Divider />
      <CustomDraggableManageColumnsMenu colDef={colDef} onClick={hideMenu} />
      <Divider />
      {!x.disableAggregation && (
        <CustomAggregationMenu colDef={colDef} onClick={hideMenu} />
      )}

    </Box>
  )
}

export default CustomColumnMenu

